import { Box, Link, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

/**
 * Build identifier shown in the footer: the short commit SHA of the deployed
 * build. CI injects the full SHA as `VITE_BUILD_VERSION` (the pipeline sets it
 * to `$(Build.SourceVersion)` — see `.azure-pipelines/web-deploy.yml`) and Vite
 * inlines it at build time. Local dev builds have no value, so we fall back to
 * `dev`. This is the deployed-commit-as-version convention — no build-number or
 * increment logic.
 */
const rawBuildVersion = import.meta.env.VITE_BUILD_VERSION?.trim();
const BUILD_VERSION = rawBuildVersion ? rawBuildVersion.slice(0, 7) : 'dev';

/**
 * The customer documentation site (`web/ami-docs`, deployed by `docs-deploy.yml`).
 *
 * Overridable per environment like the other external URLs, with the production host as
 * the default: the docs site is public and the same content serves every environment, so a
 * missing variable should still reach real documentation rather than nothing.
 */
const DOCS_URL = import.meta.env.VITE_DOCS_URL?.trim() || 'https://amidocs.acuitytech.com';

/**
 * Minimal app footer (prototype `AppFooter`): copyright · build. Sits at the end
 * of the scrolling content region (not a fixed chrome row), so it uses a top
 * margin + hairline rather than its own gutters.
 */
export function Footer() {
  const { t } = useTranslation();

  return (
    <Box
      component="footer"
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 1.75,
        justifyContent: 'space-between',
        alignItems: 'center',
        mt: 5,
        pt: 2,
        borderTop: '1px solid',
        borderColor: 'divider',
        color: 'text.secondary',
        fontSize: 12,
        lineHeight: 1.5,
      }}
    >
      <Typography sx={{ fontSize: 12, lineHeight: 1.5 }}>{t('shell.footer.copyright')}</Typography>
      {/* Build and help sit together on the right, as the prototype's shell footer had them.
          `noopener` because the target is a separate site. */}
      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1.5 }}>
        <Typography sx={{ fontSize: 12, lineHeight: 1.5 }}>
          {t('shell.footer.build', { version: BUILD_VERSION })}
        </Typography>
        <Link
          href={DOCS_URL}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ fontSize: 12, lineHeight: 1.5, fontWeight: 600, textUnderlineOffset: 2 }}
        >
          {t('shell.footer.needHelp')}
        </Link>
      </Box>
    </Box>
  );
}
